import React, { useEffect, useMemo, useState } from 'react';
import {
  Popover,
  PopoverTrigger,
  PopoverSurface,
  SearchBox,
  Tooltip,
  Spinner,
} from '@fluentui/react-components';
import { DismissRegular } from '@fluentui/react-icons';
import { usePdmAssignmentStyles } from '../../styles/PdmAssignmentPage.styles';
import {
  PDM_ROLE_ADD_LABELS,
  PDM_ROLE_LABELS,
  type PdmRole,
} from '../../constants/pdmRoleLabels';
import type { PdmAssignment, PdmDirectoryEntry } from '../../models/pdmAssignment';

interface RoleAssignmentCellProps {
  role: PdmRole;
  assignments: PdmAssignment[];
  pdmPool: PdmDirectoryEntry[];
  rowAssignedIds: Set<string>;
  onAdd: (entry: PdmDirectoryEntry) => void;
  onRemove: (assignment: PdmAssignment) => void;
  onSearch: (term: string) => Promise<PdmDirectoryEntry[]>;
  disabled?: boolean;
  busy?: boolean;
  readOnly?: boolean;
  inlineAdd?: boolean;
  addLabel?: string;
  addAriaLabel?: string;
}

/**
 * One role column cell: the assigned-person chips plus the column's `+ Add`
 * affordance and inline picker. The role is implied by the column — there is no
 * role selector on the chip or in the panel (FR-21, FR-23, FR-39).
 */
export const RoleAssignmentCell: React.FC<RoleAssignmentCellProps> = ({
  role,
  assignments,
  pdmPool,
  rowAssignedIds,
  onAdd,
  onRemove,
  onSearch,
  disabled = false,
  busy = false,
  readOnly = false,
  inlineAdd = false,
  addLabel,
  addAriaLabel,
}) => {
  const styles = usePdmAssignmentStyles();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [searchResults, setSearchResults] = useState<PdmDirectoryEntry[]>([]);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);

  const trimmed = query.trim();

  const assignedIds = useMemo(
    () => new Set(assignments.map((a) => a.pdmId)),
    [assignments],
  );

  useEffect(() => {
    if (!trimmed) {
      setSearchResults([]);
      setSearching(false);
      setSearchError(null);
      return;
    }
    let cancelled = false;
    setSearching(true);
    setSearchError(null);
    const handle = setTimeout(() => {
      onSearch(trimmed)
        .then((results) => {
          if (cancelled) return;
          setSearchResults(results);
          setSearchError(null);
        })
        .catch((err) => {
          if (cancelled) return;
          setSearchResults([]);
          setSearchError(
            err instanceof Error && err.message
              ? err.message
              : 'Directory search failed. Please try again.',
          );
        })
        .finally(() => {
          if (!cancelled) setSearching(false);
        });
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [trimmed, onSearch]);

  const options = useMemo(() => {
    const source = trimmed ? searchResults : pdmPool;
    return [...source].sort((a, b) => a.name.localeCompare(b.name));
  }, [trimmed, searchResults, pdmPool]);

  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen) {
      setQuery('');
      setSearchResults([]);
      setSearching(false);
      setSearchError(null);
    }
    setOpen(nextOpen);
  };

  const roleLabel = PDM_ROLE_LABELS[role];

  if (readOnly) {
    return assignments.length > 0 ? (
      <span>{assignments.map((a) => a.name).join(', ')}</span>
    ) : (
      <span className={styles.readOnlyEmpty}>—</span>
    );
  }

  const chipNodes = assignments.map((a) => (
    <Tooltip key={a.id} content={a.email} relationship="label" withArrow>
      <span className={styles.pdmChip}>
        <span className={styles.pdmChipLabel}>{a.name}</span>
        <button
          type="button"
          className={styles.pdmChipRemove}
          disabled={disabled}
          aria-label={`Remove ${a.name} from ${roleLabel}`}
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            e.stopPropagation();
            if (!disabled) onRemove(a);
          }}
        >
          <DismissRegular />
        </button>
      </span>
    </Tooltip>
  ));

  return (
    <div className={inlineAdd ? styles.roleCellInline : styles.roleCell}>
      {inlineAdd ? (
        chipNodes
      ) : (
        assignments.length > 0 && <div className={styles.chipRow}>{chipNodes}</div>
      )}

      <Popover
        open={open}
        onOpenChange={(_e, data) => handleOpenChange(data.open)}
        trapFocus
        positioning="below-start"
      >
        <PopoverTrigger disableButtonEnhancement>
          <button
            type="button"
            className={inlineAdd ? styles.addAffordanceInline : styles.addAffordance}
            disabled={disabled}
            aria-label={addAriaLabel ?? `${PDM_ROLE_ADD_LABELS[role]} to this partner`}
          >
            {busy ? <Spinner size="extra-tiny" /> : (addLabel ?? PDM_ROLE_ADD_LABELS[role])}
          </button>
        </PopoverTrigger>
        <PopoverSurface>
          <div className={styles.pickerSurface}>
            <SearchBox
              placeholder={`Search ${roleLabel} by name or email`}
              value={query}
              onChange={(_e, data) => setQuery(data.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') e.preventDefault();
              }}
              autoFocus
            />
            <div className={styles.pickerList} role="listbox" aria-label={`${roleLabel} options`}>
              {searching ? (
                <div className={styles.pickerEmpty}>
                  <Spinner size="tiny" label="Searching the directory…" />
                </div>
              ) : searchError ? (
                <div className={styles.pickerError} role="alert">
                  {searchError}
                </div>
              ) : options.length === 0 ? (
                <div className={styles.pickerEmpty}>
                  {trimmed
                    ? 'No matching users in the directory.'
                    : 'No PDMs assigned yet — type to search the directory.'}
                </div>
              ) : (
                options.map((p) => {
                  const already =
                    assignedIds.has(p.pdmId) || rowAssignedIds.has(p.pdmId);
                  return (
                    <button
                      key={p.pdmId}
                      type="button"
                      className={styles.pickerItem}
                      role="option"
                      aria-disabled={already}
                      aria-selected={false}
                      disabled={already}
                      onClick={() => {
                        onAdd(p);
                        setOpen(false);
                      }}
                    >
                      <span className={styles.pickerItemName}>
                        {p.name}
                        {already && (
                          <>
                            {'  '}
                            <span className={styles.alreadyAssignedTag}>already assigned</span>
                          </>
                        )}
                      </span>
                      <span className={styles.pickerItemMeta}>
                        {p.email}
                        {p.region ? `  ·  ${p.region}` : ''}
                      </span>
                    </button>
                  );
                })
              )}
            </div>
          </div>
        </PopoverSurface>
      </Popover>
    </div>
  );
};
